import React, { useEffect, useState } from "react";
import { Grid, Box, Card, CardHeader, CardContent, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";

import httpClient from "../../lib/httpClient";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { addExportButton, numberFilterOperators, tableCellRender } from "../util/tableUtils";
import { CysButtonPopover } from "../util/CysButtonPopover";

const useStyles = makeStyles(() => ({
  delimiter: {
    borderRight: "2px solid #000",
  },
}));

export const AgricultureKpi = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [agrDataFetching, setAgrDataFetching] = useState(false);
  const [agrCreditStatisticsData, setAgrCreditStatisticsData] = useState([]);
  const [agrPolygonsWeeklyData, setAgrPolygonsWeeklyData] = useState([]);
  const [agrPolygonData, setAgrPolygonData] = useState([]);
  const [agrMonthlyCreditData, setAgrMonthlyCreditData] = useState([]);
  const [agrPartitaWeeklyData, setAgrPartitaWeeklyData] = useState([]);
  const [agrRegionPartitaData, setAgrRegionPartitaData] = useState([]);

  const addIdToCollection = (collection) => {
    return collection.map((item, index) => ({ id: index, ...item }));
  };

  useEffect(() => {
    const getAgrKpiData = async () => {
      const result = await Promise.all([
        httpClient().get(`/kpi/agrCreditStatistics`),
        httpClient().get(`/kpi/agrPolygonsWeekly`),
        httpClient().get(`/kpi/agrPolygon`),
        httpClient().get(`/kpi/agrMonthlyCredit`),
        httpClient().get(`/kpi/agrPartitaWeekly`),
        httpClient().get(`/kpi/agrRegionPartita`),
      ]);

      setAgrCreditStatisticsData(addIdToCollection(result[0].data));
      setAgrPolygonsWeeklyData(addIdToCollection(result[1].data));
      setAgrPolygonData(addIdToCollection(result[2].data));
      setAgrMonthlyCreditData(addIdToCollection(result[3].data));
      setAgrPartitaWeeklyData(addIdToCollection(result[4].data));
      setAgrRegionPartitaData(addIdToCollection(result[5].data));
    };

    setAgrDataFetching(true);
    getAgrKpiData();
    setAgrDataFetching(false);
  }, []);

  const populateColumns = (columns) =>
    columns.map((column) => ({
      ...column,
      flex: 1,
      valueFormatter: (params) => tableCellRender(params.value),
    }));

  const agrCreditStatisticsColumns = [
    {
      field: "timestamp",
      headerName: "Timestamp",
      width: 200,
    },
    {
      field: "kind",
      headerName: "Kind",
    },
    {
      field: "requests",
      headerName: "Requests",
      filterOperators: numberFilterOperators,
    },
    {
      field: "failures",
      headerName: "Failures",
      filterOperators: numberFilterOperators,
    },
    {
      field: "percentage",
      headerName: "Percentage of failures",
      filterOperators: numberFilterOperators,
    },
  ];

  const agrPolygonsWeeklyColumns = [
    {
      field: "weekFrom",
      headerName: "Week From",
    },
    { field: "weekTo", headerName: "Week To", flex: 1 },
    {
      field: "mappaliWithPolygonsFound",
      headerName: "Mappali With Polygons Found",
    },
    {
      field: "mappaliWithPolygonsNotFound",
      headerName: "Mappali With Polygons Not Found",
      filterOperators: numberFilterOperators,
    },
    {
      field: "percentageOfNotFound",
      headerName: "Percentage Of Not Found",
      filterOperators: numberFilterOperators,
    },
  ];

  const agrPolygonsColumns = [
    {
      field: "region",
      headerName: "Region",
    },
    {
      field: "mappaliWithPolygonsFound",
      headerName: "Mappali With Polygons Found",
      filterOperators: numberFilterOperators,
    },
    {
      field: "mappaliWithPolygonsNotFound",
      headerName: "Mappali With Polygons Not Found",
      filterOperators: numberFilterOperators,
    },
    {
      field: "percentage",
      headerName: "Percentage of not found",
      filterOperators: numberFilterOperators,
    },
  ];

  const agrMonthlyCreditColumns = [
    {
      field: "monthAndYear",
      headerName: "Month And Year",
    },
    {
      field: "normalRequests",
      headerName: "Normal Requests",
      filterOperators: numberFilterOperators,
    },

    {
      field: "normalFailures",
      headerName: "Normal Failures",
      filterOperators: numberFilterOperators,
    },

    {
      field: "normalFailurePercentage",
      headerName: "Normal Failure Percentage",
      filterOperators: numberFilterOperators,
    },

    {
      field: "voyagerRequests",
      headerName: "Voyager Requests",
      filterOperators: numberFilterOperators,
    },

    {
      field: "voyagerFailures",
      headerName: "Voyager Failures",
      filterOperators: numberFilterOperators,
    },
    {
      field: "voyagerFailurePercentage",
      headerName: "Voyager Failure Percentage",
      filterOperators: numberFilterOperators,
    },
  ];

  const agrPartitaWeeklyColumns = [
    {
      field: "weekFrom",
      headerName: "Week From",
    },
    { field: "weekTo", headerName: "Week To", flex: 1 },

    {
      field: "partitasOk",
      headerName: "Complete Partitas",
      filterOperators: numberFilterOperators,
    },
    {
      field: "partitasWithMissingSezione",
      headerName: "Partitas With Missing Sezione",
      filterOperators: numberFilterOperators,
    },
    {
      field: "partitasWithUnrecognizedText",
      headerName: "Partitas With Unrecognized Text",
      filterOperators: numberFilterOperators,
    },
  ];

  const currentYear = new Date().getFullYear();
  const years = ["Total"];

  for (let year = currentYear; year >= 2017; year--) {
    years.push(year.toString());
  }
  const categories = ["ok", "unrecognizedText", "missingSezione"];

  const columnsTotal = [];
  const columnsYears = [];
  const regionColumn = {
    field: "region",
    headerName: "region",
  };

  years.forEach((year) => {
    categories.forEach((category) => {
      const currentRow = {
        field: category + year,
        headerName: year + " " + category,
        valueFormatter: (params) => tableCellRender(params.value),
      };

      if (year !== "Total" && category === "missingSezione") {
        currentRow.cellClassName = classes.delimiter;
      }

      if (year === "Total") {
        columnsTotal.push(currentRow);
      } else {
        columnsYears.push(currentRow);
      }
    });
  });

  const agrRegionPartitaColumns = [regionColumn, ...columnsYears];
  const agrRegionPartitaTotalColumns = [regionColumn, ...columnsTotal];

  const getPopulatedTable = ({ title, rows, columns, exportTitle, extraToolbarWidget }) => (
    <Grid item xs={12} sm={12}>
      <Box mb={3}>
        <Typography variant="body1" gutterBottom>
          {title}
        </Typography>
        <DataGridCystellarTable
          rows={rows}
          columns={columns}
          getRowId={(rowData) => rowData.id}
          loading={agrDataFetching}
          pagination={false}
          customToolbarComp={true}
          customToolbarWidgets={[addExportButton(exportTitle), extraToolbarWidget]}
        />
      </Box>
    </Grid>
  );

  const tooltipInfoMsg = (
    <Box m={1}>
      <ul>
        <li> {t("kpiDashboard.normalOperation")}</li>
        <li> {t("kpiDashboard.voyagerOperation")} </li>
      </ul>
    </Box>
  );

  return (
    <Card>
      <CardHeader title="Agriculture mappali geolocation KPI" />
      <CardContent>
        <Grid container spacing={1}>
          <h3>1. Cleaning</h3>
          {getPopulatedTable({
            title:
              "Cleaning of partita data for all the policies during the latest processing (number of partitas). It is performed daily.   The 'unrecognized text' is reported when incorrect text is used instead of foglio and mappale. The 'missing sezione' is reported when a partita must have a sezione of comune, but it is missing and cannot be guessed.",
            rows: agrRegionPartitaData,
            columns: populateColumns(agrRegionPartitaTotalColumns),
            exportTitle: "Agriculture Region Partita Data Total",
          })}

          {getPopulatedTable({
            title:
              "Cleaning of partita data for policies during the latest processing (number of partitas). It is performed daily. The 'unrecognized text' is reported when incorrect text is used instead of foglio and mappale. The 'missing sezione' is reported when a partita must have a sezione of comune, but it is missing and cannot be guessed. The year here is the year of the policy.",
            rows: agrRegionPartitaData,
            columns: populateColumns(agrRegionPartitaColumns),
            exportTitle: "Agriculture Region Partita Data Per Years",
          })}

          {getPopulatedTable({
            title: "Cleaning of partita data, weekly",
            rows: agrPartitaWeeklyData,
            columns: populateColumns(agrPartitaWeeklyColumns),
            exportTitle: "Agriculture Partite Weekly Data",
          })}

          <h3>2. Obtaining center</h3>
          {getPopulatedTable({
            title:
              "Credit Statistics Data regarding each request to Stimatrix API  (number of unique mappali)",
            rows: agrCreditStatisticsData,
            columns: populateColumns(agrCreditStatisticsColumns),
            exportTitle: "Agriculture Credit Statistics",
            extraToolbarWidget: (
              <CysButtonPopover buttonMsg="table info" popoverText={tooltipInfoMsg}></CysButtonPopover>
            ),
          })}

          {getPopulatedTable({
            title: "Monthly Credit Data regarding the requests to Stimatrix API",
            rows: agrMonthlyCreditData,
            columns: populateColumns(agrMonthlyCreditColumns),
            exportTitle: "Agriculture Monthly Credit Data",
          })}

          <h3>3. Computing polygon</h3>
          {getPopulatedTable({
            title:
              "Success and failure of finding mappali polygons for each region during the latest geolocation process (number of unique mappali)",
            rows: agrPolygonData,
            columns: populateColumns(agrPolygonsColumns),
            exportTitle: "Agriculture Polygons Data",
          })}

          {getPopulatedTable({
            title: "Mappali Polygons Geolocation Weekly Data",
            rows: agrPolygonsWeeklyData,
            columns: populateColumns(agrPolygonsWeeklyColumns),
            exportTitle: "Agriculture Polygons Weekly Data",
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};
